import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Textarea } from "../ui/textarea";

const CORRECTION_TYPES = [
  "OCR_MISREAD", "MISSING_CLOCK_IN", "MISSING_CLOCK_OUT", "EMPLOYEE_FORGOT_TO_CLOCK",
  "AUTHORIZED_EARLY_START", "AUTHORIZED_LATE_FINISH", "BREAK_CORRECTION",
  "CROSS_MIDNIGHT_CORRECTION", "SCHEDULE_CHANGE", "DUPLICATE_PUNCH", "OTHER",
];

/** Replaces the old inline `fixed inset-0` modal with a proper Shadcn Dialog. Shows the
 * original AI-extracted value as read-only alongside the editable corrected value, so a
 * reviewer always sees exactly what they are overriding - the correction record itself already
 * stores both (see `correct_day_entry`'s `original_clock_in`/`original_clock_out`). */
export const CorrectionDialog = ({ open, onOpenChange, row, onSubmit }) => {
  const [form, setForm] = useState({ new_clock_in: "", new_clock_out: "", correction_type: "OTHER", reason: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && row) {
      setForm({ new_clock_in: row.manager_approved_clock_in || row.extracted_clock_in || "", new_clock_out: row.manager_approved_clock_out || row.extracted_clock_out || "", correction_type: "OTHER", reason: "" });
    }
  }, [open, row]);

  if (!row) return null;

  const handleSave = async () => {
    if (!form.reason.trim()) return;
    setSubmitting(true);
    try {
      await onSubmit(row.date, form);
      onOpenChange(false);
    } catch (e) {
      // Error already surfaced via toast by the parent action helper.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !submitting && onOpenChange(next)}>
      <DialogContent className="max-w-md" data-testid="correction-modal">
        <DialogHeader>
          <DialogTitle>Correct {row.date}</DialogTitle>
          <DialogDescription>The AI-extracted value stays on record as the original - this only records what a reviewer approved instead, with a reason.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 bg-gray-50 rounded-lg p-2.5">
          <div>
            <p className="text-[10px] text-gray-500 uppercase font-bold">Original (OCR)</p>
            <p className="text-sm text-gray-500">{row.extracted_clock_in || "?"} - {row.extracted_clock_out || "?"}</p>
          </div>
          <div>
            <p className="text-[10px] text-gray-500 uppercase font-bold">Confidence</p>
            <p className="text-sm text-gray-500">{row.extraction_confidence || "-"}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs text-gray-500">Corrected clock in</label>
            <input data-testid="correction-clock-in-input" value={form.new_clock_in} onChange={(e) => setForm({ ...form, new_clock_in: e.target.value })} placeholder="HH:MM" className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm" />
          </div>
          <div>
            <label className="text-xs text-gray-500">Corrected clock out</label>
            <input data-testid="correction-clock-out-input" value={form.new_clock_out} onChange={(e) => setForm({ ...form, new_clock_out: e.target.value })} placeholder="HH:MM" className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm" />
          </div>
        </div>
        <div>
          <label className="text-xs text-gray-500">Correction type</label>
          <select data-testid="correction-type-select" value={form.correction_type} onChange={(e) => setForm({ ...form, correction_type: e.target.value })} className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm">
            {CORRECTION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-500">Reason (required - add any extra notes here too)</label>
          <Textarea data-testid="correction-reason-input" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} rows={3} />
        </div>

        <DialogFooter>
          <button data-testid="cancel-correction-btn" onClick={() => onOpenChange(false)} disabled={submitting} className="px-3 py-1.5 rounded text-xs font-semibold border border-gray-200 hover:bg-gray-50">Cancel</button>
          <button data-testid="save-correction-btn" onClick={handleSave} disabled={submitting || !form.reason.trim()} className="px-3 py-1.5 rounded text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
            {submitting ? "Saving..." : "Save Correction"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
